import { Grid } from '@mui/material';
import PaymentApi from 'api/counter/payment/payment.api';
import { resetModal } from 'app/slice/modal.slice';
import { swalCustom } from 'component/Alert/Swal';
import { notiError, notiSuccess } from 'component/notifications/notifications';
import TextAreaInput from 'new-components/inputs/text-area-input';
import TextInput from 'new-components/inputs/text-input';
import PageModal from 'new-components/modal/page-modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getBranch } from 'utils/app.utils';

export interface ICancelPaymentProps {
    isShow: boolean
    id: number
    paymentNumber: string
    onReset?: () => void
    resetSuccess?: (status: boolean) => void
    checkApprovalPermiss?: boolean
    setPopupCancel?: React.Dispatch<React.SetStateAction<boolean>>;
}
const initStateErrorMessage = {
    USER_NAME_STRING_EMPTY: '',
    PASSWORD_STRING_EMPTY: '',
    CANCEL_REMARK_STRING_EMPTY: '',
    NOT_FOUND_USER: '',
}
export default function CancelPayment(props: ICancelPaymentProps) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [username, setUserCode] = useState<string>('')
    const [password, setPassword] = useState<string>('');
    const [cancelRemark, setCancelRemark] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<any>({ ...initStateErrorMessage })

    const [disabledDel, setDisabledDel] = useState<boolean>(false)

    const clearErrorMessage = async () => setErrorMessage(initStateErrorMessage)
    const resetForm = async () => {
        clearErrorMessage()
        if (props.onReset) props.onReset()
        dispatch(resetModal())
        setUserCode('')
        setPassword('')
        setCancelRemark('')
    }

    useEffect(() => {
        clearErrorMessage()
    }, [username, password, cancelRemark]);

    const onDelete = (id: number, obj: any) => {
        if (disabledDel) {
            return
        }
        setDisabledDel(true)
        props.resetSuccess && props.resetSuccess(false)
        if (!username || !password || !cancelRemark) {
            setErrorMessage({
                USER_NAME_STRING_EMPTY: !username ? t('PAYMENT.MESSAGE.ERROR.USER_NAME_STRING_EMPTY') : '',
                PASSWORD_STRING_EMPTY: !password ? t('PAYMENT.MESSAGE.ERROR.PASSWORD_STRING_EMPTY') : '',
                CANCEL_REMARK_STRING_EMPTY: !cancelRemark ? t('PAYMENT.MESSAGE.ERROR.CANCEL_REMARK_STRING_EMPTY') : '',
            })
            setDisabledDel(false)
            return false
        }

        if (!props?.checkApprovalPermiss) {
            PaymentApi.cancel(id, { username, password, cancelRemark })
                .then((res) => {
                    notiSuccess(t(`PAYMENT.MESSAGE.SUCCESS.CANCEL`))
                    dispatch(resetModal())
                    props.resetSuccess && props.resetSuccess(true)
                })
                .catch((e) => {
                    const message = e?.response?.data?.message
                    if (message === 'NOT_FOUND_USER' || message === 'INVALID_PASSWORD') {
                        setErrorMessage({
                            ...initStateErrorMessage,
                            NOT_FOUND_USER: message === 'NOT_FOUND_USER' ? t('PAYMENT.MESSAGE.ERROR.NOT_FOUND_USER') : '',
                            INVALID_PASSWORD: message === 'INVALID_PASSWORD' ? t('PAYMENT.MESSAGE.ERROR.INVALID_PASSWORD') : ''
                        })
                    } else notiError(t('PAYMENT.MESSAGE.ERROR.NOT_CANCEL'))
                }).finally(() => setDisabledDel(false))
        } else {
            PaymentApi.cancelNew(id, { username, password, requestReason: cancelRemark, branchId: getBranch() })
                .then((res) => {
                    notiSuccess(t(`PAYMENT.MESSAGE.SUCCESS.CANCEL`))
                    dispatch(resetModal())
                    swalCustom(
                        t('APPROVELLIST.FILTER.PENDING'),
                        `<div class="test-t" style="width: 350px">${t('APPROVELLIST.MESSAGE.PENDING_RECEIPT')}</div>`,
                        (res: any) => props.resetSuccess && props.resetSuccess(true),
                        'warning',
                        {
                            confirmButton: { showConfirmButton: false },
                            cancelButton: { cancelButtonText: t('BUTTON.ENTER') },
                            showCloseButton: false
                        }
                    )
                })
                .catch((e) => {
                    const message = e?.response?.data?.message
                    if (message === 'NOT_FOUND_USER' || message === 'INVALID_PASSWORD') {
                        setErrorMessage({
                            ...initStateErrorMessage,
                            NOT_FOUND_USER: message === 'NOT_FOUND_USER' ? t('PAYMENT.MESSAGE.ERROR.NOT_FOUND_USER') : '',
                            INVALID_PASSWORD: message === 'INVALID_PASSWORD' ? t('PAYMENT.MESSAGE.ERROR.INVALID_PASSWORD') : ''
                        })
                    } else notiError(t('PAYMENT.MESSAGE.ERROR.NOT_CANCEL'))
                }).finally(() => { setDisabledDel(false); props.resetSuccess && props.resetSuccess(true) })
        }
    }

    return props.isShow ? (
        <PageModal
            isOpen
            size='S'
            title={t('PAYMENT.FORM.TITLE')}
            handleClose={() => {
                resetForm()
                props.setPopupCancel && props.setPopupCancel(false)
            }}
            onCancel={{
                onClick: () => {
                    props.setPopupCancel && props.setPopupCancel(false)
                    resetForm()
                }, title: t('BUTTON.CANCEL')
            }}
            onSubmit={{ onClick: () => onDelete(props.id, {}), title: t('BUTTON.SUBMIT'), type: 'danger' }}
        >
            <Grid container spacing={2}>
                <Grid item className={`w-100`}>
                    {t('PAYMENT.FORM.CAPTION')} {props.paymentNumber}
                </Grid>
                <Grid item md={6} className={`w-100`}>
                    <TextInput
                        required
                        fullWidth
                        label={t('PAYMENT.FORM.USER_NAME')}
                        value={username}
                        helperText={errorMessage?.USER_NAME_STRING_EMPTY || errorMessage?.NOT_FOUND_USER || ''}
                        onChange={(e) => setUserCode(e.target.value)}
                    />
                </Grid>
                <Grid item md={6} className={`w-100`}>
                    <TextInput
                        required
                        fullWidth
                        label={t('PAYMENT.FORM.PASSWORD')}
                        type="password"
                        value={password}
                        helperText={errorMessage?.PASSWORD_STRING_EMPTY || errorMessage?.INVALID_PASSWORD || ''}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </Grid>
                <Grid item className={`w-100`}>
                    <TextAreaInput fullWidth required value={cancelRemark} onChange={(event) => setCancelRemark(event.target.value)} label={t('PAYMENT.FORM.CANCEL_REMARK')} helperText={errorMessage?.CANCEL_REMARK_STRING_EMPTY || ''} />
                </Grid>
            </Grid>
        </PageModal>
    ) : <></>
}
